import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, MapPin, Package, Printer, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_CONFIG } from '@/shared/constants/api';
import HttpService from '@/shared/services/http.service';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { logEvent } from '@/analytics';

const SlotResult = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get('packageId') || '??';
  const packageNumber = searchParams.get('packageNumber') || '';
  const POLL_INTERVAL_MS = 2000;

  const [assigningSlot, setAssigningSlot] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [packageInfo, setPackageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [frameNumber, setFrameNumber] = useState('');
  const [storageOption, setStorageOption] = useState<'CENTER' | 'LARGE' | 'SAFE' | ''>('');
  const [frameOpen, setFrameOpen] = useState(false);
  const [receiverName, setReceiverName] = useState('');
  const [frameSearchInput, setFrameSearchInput] = useState('');
  const [boxNumber, setBoxNumber] = useState('');
  const [boxOpen, setBoxOpen] = useState(false);
  const [boxSearchInput, setBoxSearchInput] = useState('');
  const [notes, setNotes] = useState('');
  const [showAssignChoice, setShowAssignChoice] = useState(false);
  const frameFieldRef = useRef<HTMLDivElement>(null);

  // Fetch package data on mount, polling until OCR processing completes
  useEffect(() => {
    if (!packageId || packageId === '??') {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const fetchPackageData = async () => {
      try {
        const response = await HttpService.get(`${API_CONFIG.path.packageList}/${packageId}`);
        if (cancelled) return;

        const data = response.data || response;
        if (data && data.id) {
          const metadata = data.packageMetadata || data.metadata || {};
          setPackageInfo({ ...metadata, trackingNumbers: data.barcodes });

          // Auto-fill receiver name if available
          if (metadata.receiver?.name) {
            setReceiverName(metadata.receiver.name);
          }

          // Auto-fill other slot details if they are already assigned
          if (data.frameNumber) setFrameNumber(data.frameNumber);
          if (data.boxNumber) setBoxNumber(data.boxNumber);
          if (data.storageOption) setStorageOption(data.storageOption);
          if (metadata.customNote) setNotes(metadata.customNote);

          if (data.ocrStatus !== 'completed') {
            timeoutId = setTimeout(fetchPackageData, POLL_INTERVAL_MS);
            return;
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching package data:', err);
        if (!cancelled) setLoading(false);
      }
    };

    fetchPackageData();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [packageId]);

  // const [status, setStatus] = useState('Ready to print');
  // console.log('🚀 ~ SlotResult ~ status-------->', status);

  // useEffect(() => {
  //   const handleAfterPrint = () => {
  //     console.log('Print dialog closed');
  //     setStatus('Print dialog closed');

  //     // Your next action here
  //     // Example:
  //     // navigate("/dashboard");
  //     // callApi();
  //     // closeModal();
  //   };

  //   window.addEventListener('afterprint', handleAfterPrint);

  //   return () => {
  //     window.removeEventListener('afterprint', handleAfterPrint);
  //   };
  // }, []);

  // const handlePrint = () => {
  //   setStatus('Opening print dialog...');
  //   window.print();
  // };

  const noStorageSelected = !frameNumber && !boxNumber && !storageOption;

  // Reset the assign/continue choice back to the normal Assign button once a storage option is selected
  useEffect(() => {
    if (!noStorageSelected) {
      setShowAssignChoice(false);
    }
  }, [noStorageSelected]);

  const handleAssignButtonClick = () => {
    if (noStorageSelected) {
      setShowAssignChoice(true);
      return;
    }
    logEvent('slot_assigned_via_storage_selection', { flow: 'direct_storage_selection', packageId, packageNumber });
    handleDone();
  };

  const handleUpdateStorageSelection = () => {
    logEvent('storage_selection_updated', { packageId, packageNumber });
    setShowAssignChoice(false);
    frameFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleContinueToAssign = () => {
    logEvent('slot_assigned_without_storage_selection', { flow: 'continue_without_storage', packageId, packageNumber });
    handleDone();
  };

  const handleDone = async () => {
    // Close any open popovers first so Radix's portal unmounts cleanly
    // before route navigation unmounts this page (avoids a removeChild race).
    setFrameOpen(false);
    setBoxOpen(false);
    setAssigningSlot(true);
    setError(null); // Clear previous errors

    try {
      // Call API to assign slot with frame number, box number, and storage option
      await HttpService.put(`${API_CONFIG.path.outboundFlow}/${packageId}/assign-slot`, {
        frameNumber,
        boxNumber,
        storageOption,
        receiverName,
        customNote: notes,
      });
      const fromPath = searchParams.get('from');
      if (fromPath) {
        navigate(fromPath);
      } else {
        navigate('/'); // Only redirect on success
      }
    } catch (err) {
      console.error('Error assigning slot:', err);
      setError(t('failed_to_assign_slot'));
    } finally {
      setAssigningSlot(false);
    }
  };

  return (
    <>
      <div className="min-h-[calc(100vh-73px)] bg-background flex flex-col items-center justify-center p-6 relative print:hidden">
        <div className="absolute top-4 left-4 z-10">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setFrameOpen(false);
              setBoxOpen(false);
              setTimeout(() => navigate(-1), 0);
            }}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </div>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="text-center max-w-md w-full"
        >
          {/* Success/Error icon */}
          {!error ? (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-20 h-20 rounded-full bg-success flex items-center justify-center mx-auto mb-4 animate-pulse-success"
            >
              <Check className="w-10 h-10 text-success-foreground" />
            </motion.div>
          ) : (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-20 h-20 rounded-full bg-destructive flex items-center justify-center mx-auto mb-6"
            >
              <X className="w-10 h-10 text-destructive-foreground" />
            </motion.div>
          )}

          {!error ? (
            <p className="text-muted-foreground text-sm mb-2">{t('assigned_slot')}</p>
          ) : (
            <p className="text-destructive text-sm mb-2">{t('assignment_failed')}</p>
          )}

          {/* Huge slot number */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="slot-display mb-4 flex items-center justify-center gap-3 print:hidden"
          >
            {packageNumber}
            {/* Print sticker feature — commented out for now
            <button
              type="button"
              aria-label={t('print_sticker')}
              title={t('print_sticker')}
              onClick={handlePrint}
              className="w-9 h-9 rounded-full bg-muted hover:bg-muted/70 text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors shrink-0"
            >
              <Printer className="w-5 h-5" />
            </button>
            */}
          </motion.div>

          {/* Visual cue */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex items-center justify-center gap-2 text-muted-foreground mb-2"
          >
            <MapPin className="w-5 h-5" />
            <span className="text-base font-medium">
              {t('parcel_number')} {packageNumber}
            </span>
          </motion.div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
            >
              <div className="flex items-start gap-3">
                <div className="text-red-600">
                  <X className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-red-800 font-medium">{t('assignment_failed')}</p>
                  <p className="text-red-700 text-sm mt-1">{error}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Frame Number and Storage Option */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-card rounded-lg p-4 mb-4"
          >
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {t('location_details')}
            </h3>
            <div className="space-y-3">
              {/* Frame Number Searchable Combobox */}
              <div ref={frameFieldRef}>
                <label htmlFor="frameNumber" className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('frame_number')}
                </label>
                <Popover open={frameOpen} onOpenChange={setFrameOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={frameOpen}
                      className="w-full justify-between text-left font-normal relative pr-10"
                      disabled={assigningSlot}
                    >
                      <span className="truncate">
                        {frameNumber ? `F${frameNumber.replace('F', '')}` : t('select_frame')}
                      </span>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                        {frameNumber && (
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label={t('clear')}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFrameNumber('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                setFrameNumber('');
                              }
                            }}
                            className="h-4 w-4 rounded-full flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                          >
                            <X className="h-3 w-3" />
                          </span>
                        )}
                        <Search className="h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t('type_eg_f1_f25')}
                        value={frameSearchInput}
                        onValueChange={(value) => {
                          // Allow direct input, format as F[number] if just number is entered
                          if (value && !value.startsWith('F')) {
                            const num = parseInt(value);
                            if (!isNaN(num) && num > 0 && num <= 50) {
                              setFrameSearchInput(`F${num}`);
                            } else {
                              setFrameSearchInput(value);
                            }
                          } else {
                            setFrameSearchInput(value);
                          }
                        }}
                      />
                      <CommandList>
                        <CommandEmpty>
                          <div className="p-2">
                            <p className="text-sm text-muted-foreground mb-2">{t('no_record_found')}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const inputValue = frameSearchInput.replace('F', '');
                                const num = parseInt(inputValue);
                                if (!isNaN(num) && num > 0 && num <= 999) {
                                  setFrameNumber(`F${num}`);
                                  setFrameSearchInput('');
                                  setFrameOpen(false);
                                }
                              }}
                              className="w-full"
                            >
                              {t('use')} "{frameSearchInput}"
                            </Button>
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {Array.from({ length: 50 }, (_, i) => i + 1)
                            .filter((num) => {
                              const searchTerm = frameSearchInput.replace('F', '').toLowerCase();
                              return num.toString().includes(searchTerm);
                            })
                            .map((num) => (
                              <CommandItem
                                key={num}
                                value={`F${num}`}
                                onSelect={(currentValue) => {
                                  setFrameNumber(currentValue);
                                  setFrameSearchInput('');
                                  setFrameOpen(false);
                                }}
                              >
                                F{num}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground mt-1">{t('search_or_select_the_1')}</p>
              </div>

              {/* Box Number Searchable Combobox */}
              <div>
                <label htmlFor="boxNumber" className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('box_number')}
                </label>
                <Popover open={boxOpen} onOpenChange={setBoxOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={boxOpen}
                      className="w-full justify-between text-left font-normal relative pr-10"
                      disabled={assigningSlot}
                    >
                      <span className="truncate">{boxNumber ? boxNumber : t('select_box')}</span>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                        {boxNumber && (
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label={t('clear')}
                            onClick={(e) => {
                              e.stopPropagation();
                              setBoxNumber('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                setBoxNumber('');
                              }
                            }}
                            className="h-4 w-4 rounded-full flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                          >
                            <X className="h-3 w-3" />
                          </span>
                        )}
                        <Search className="h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t('type_eg_box1_box25')}
                        value={boxSearchInput}
                        onValueChange={(value) => {
                          // Allow direct input, format as Box-[number] if just number is entered
                          if (value && !value.startsWith('Box-')) {
                            const cleaned = value.replace('B', '').replace('Box-', '');
                            const num = parseInt(cleaned);
                            if (!isNaN(num) && num > 0 && num <= 50) {
                              setBoxSearchInput(`Box-${num}`);
                            } else {
                              setBoxSearchInput(value);
                            }
                          } else {
                            setBoxSearchInput(value);
                          }
                        }}
                      />
                      <CommandList>
                        <CommandEmpty>
                          <div className="p-2">
                            <p className="text-sm text-muted-foreground mb-2">{t('no_record_found')}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const inputValue = boxSearchInput.replace('Box-', '').replace('B', '');
                                const num = parseInt(inputValue);
                                if (!isNaN(num) && num > 0 && num <= 999) {
                                  setBoxNumber(`Box-${num}`);
                                  setBoxSearchInput('');
                                  setBoxOpen(false);
                                }
                              }}
                              className="w-full"
                            >
                              {t('use')} "{boxSearchInput}"
                            </Button>
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {Array.from({ length: 50 }, (_, i) => i + 1)
                            .filter((num) => {
                              const searchTerm = boxSearchInput.replace('Box-', '').replace('B', '').toLowerCase();
                              return num.toString().includes(searchTerm);
                            })
                            .map((num) => (
                              <CommandItem
                                key={num}
                                value={`Box-${num}`}
                                onSelect={(currentValue) => {
                                  setBoxNumber(currentValue);
                                  setBoxSearchInput('');
                                  setBoxOpen(false);
                                }}
                              >
                                Box-{num}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground mt-1">{t('search_or_select_the')}</p>
              </div>

              {/* Storage Option Tabs */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('storage_option')}</label>
                <div className="flex gap-1 bg-muted p-1 rounded-lg">
                  {(['CENTER', 'LARGE', 'SAFE'] as const).map((option) => (
                    <motion.button
                      key={option}
                      type="button"
                      onClick={() => setStorageOption(storageOption === option ? '' : option)}
                      className={cn(
                        'flex-1 py-2 px-3 text-sm font-medium rounded-md',
                        storageOption === option
                          ? 'bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/20'
                          : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
                      )}
                      disabled={assigningSlot}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      animate={{
                        backgroundColor: storageOption === option ? 'hsl(var(--primary))' : 'transparent',
                        color:
                          storageOption === option ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))',
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 25,
                      }}
                    >
                      {t(option.toLowerCase())}
                    </motion.button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t('select_storage_type_if')}</p>
              </div>

              {/* Receiver Name Input */}
              <div>
                <label htmlFor="receiverName" className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('receiver_name')}
                </label>
                <input
                  id="receiverName"
                  type="text"
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  className="w-full px-3 py-2 border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md"
                  placeholder={t('enter_receiver_name')}
                  disabled={assigningSlot || loading}
                />
                <p className="text-xs text-muted-foreground mt-1">{t('autofilled_from_package_data')}</p>
              </div>

              {/* Notes Textarea */}
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('notes')}
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.replace(/^\s+/, '').toUpperCase())}
                  className="w-full px-3 py-2 border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md resize-none"
                  placeholder={t('enter_notes')}
                  rows={3}
                  disabled={assigningSlot}
                />
              </div>
            </div>
          </motion.div>

          {/* Package Details */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-card rounded-lg p-4 mb-4 text-left"
          >
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              {t('package_details')}
            </h3>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">{t('processing_package_details')}</p>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('carrier')}</span>
                  <span className="font-medium">{packageInfo?.carrier || t('na')}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('package_count')}</span>
                  <span className="font-medium">{packageInfo?.packageCount || t('na')}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('weight')}</span>
                  <span className="font-medium">
                    {packageInfo?.weight?.value || 0} {packageInfo?.weight?.unit || 'kg'}
                  </span>
                </div>

                <div className="border-t pt-2 mt-2">
                  <p className="text-muted-foreground text-xs mb-1">{t('from')}</p>
                  <p className="font-medium">{packageInfo?.sender?.name || t('na')}</p>
                  {(packageInfo?.sender?.address || packageInfo?.sender?.postalCode) && (
                    <p className="text-xs text-muted-foreground">
                      {packageInfo?.sender?.address}, {packageInfo?.sender?.postalCode}
                    </p>
                  )}
                </div>

                <div className="border-t pt-2 mt-2">
                  <p className="text-muted-foreground text-xs mb-1">{t('to')}</p>
                  <p className="font-medium">{packageInfo?.receiver?.name || t('na')}</p>
                  {(frameNumber || boxNumber) && (
                    <p className="text-sm text-muted-foreground mb-4 font-mono">
                      {[frameNumber && `Frame: ${frameNumber}`, boxNumber].filter(Boolean).join(' | ')}
                    </p>
                  )}
                  {(packageInfo?.receiver?.address || packageInfo?.receiver?.postalCode) && (
                    <p className="text-xs text-muted-foreground">
                      {packageInfo?.receiver?.address}, {packageInfo?.receiver?.postalCode}
                    </p>
                  )}
                </div>

                {packageInfo?.trackingNumbers?.length > 0 && (
                  <div className="border-t pt-2 mt-2">
                    <p className="text-muted-foreground text-xs mb-1">{t('barcodes')}</p>
                    <p className="font-mono text-xs">{packageInfo.trackingNumbers[0] || t('na')}</p>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          <div className="space-y-3">
            {loading && (
              <p className="text-sm text-muted-foreground font-medium text-center">
                {t('package_details_loading_warning')}
              </p>
            )}
            {noStorageSelected && showAssignChoice && (
              <p className="text-sm text-amber-600 font-medium text-center">{t('no_storage_selected_warning')}</p>
            )}
            {noStorageSelected && showAssignChoice ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full"
                  onClick={handleUpdateStorageSelection}
                  disabled={assigningSlot || loading}
                >
                  {t('update_storage_selection')}
                </Button>
                <Button
                  variant="action"
                  size="lg"
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={handleContinueToAssign}
                  disabled={assigningSlot || loading}
                >
                  {assigningSlot && (
                    <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                  )}
                  <span>{assigningSlot ? `${t('assigning')}...` : t('continue_to_assign')}</span>
                </Button>
              </div>
            ) : (
              <Button
                variant="action"
                size="lg"
                className="w-full"
                onClick={handleAssignButtonClick}
                disabled={assigningSlot || loading}
              >
                {assigningSlot ? (
                  <>
                    <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                    {t('assigning')}
                    {[frameNumber, boxNumber].filter(Boolean).length > 0
                      ? ` ${t('to_lower')} ${[frameNumber, boxNumber].filter(Boolean).join(' & ')}`
                      : ''}
                    ...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    {t('assign')}
                    {[frameNumber, boxNumber].filter(Boolean).length > 0
                      ? ` ${t('to_lower')} ${[frameNumber, boxNumber].filter(Boolean).join(' & ')}`
                      : ''}
                  </>
                )}
              </Button>
            )}

            {/*  <Button variant="outline" size="xl" className="w-full" onClick={() => navigate('/scan?mode=inbound')}>
            <ArrowRight className="w-5 h-5" />
            Back To Home Page
          </Button> */}
          </div>
        </motion.div>
      </div>

      {/* Print-only sticker — commented out along with the print sticker feature
      {createPortal(
        <div className="hidden print:flex print-sticker">
          <span>{packageNumber}</span>
        </div>,
        document.body,
      )}
      */}
    </>
  );
};

export default SlotResult;
